import { Box, TextField, SxProps, Theme, Typography } from '@mui/material';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

type OtpInputProps = Readonly<{
  controllerField: ControllerRenderProps<FieldValues, string>;
  error?: string;
}>;

export const OtpInput = ({ controllerField, error }: OtpInputProps) => {
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '');

    if (paste.length > 0) {
      const otp = paste.slice(0, 6);

      controllerField.onChange(otp);

      const lastIndex = otp.length - 1;
      const lastInput = document.getElementById(`otp-input-${lastIndex}`);

      lastInput?.focus();
    }
  };

  return (
    <Box sx={otpContainerSx}>
      {Array.from({ length: 6 }).map((_, index) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;

          if (!/^\d?$/.test(value)) return;

          const codeArray = controllerField.value
            ? controllerField.value.split('')
            : ['', '', '', ''];

          codeArray[index] = value;
          controllerField.onChange(codeArray.join(''));

          if (value && index < 5) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`);

            nextInput?.focus();
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Backspace' && !controllerField.value?.[index] && index > 0) {
            const prevInput = document.getElementById(`otp-input-${index - 1}`);

            prevInput?.focus();
          }
        };

        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              key={index}
              id={`otp-input-${index}`}
              value={controllerField.value?.[index] || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              inputProps={{
                maxLength: 1,
              }}
              sx={textFieldSx}
              error={!!error}
              onPaste={handlePaste}
            />
            {index === 2 && (
              <Typography variant="display-xl-medium" sx={dashSx}>
                -
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const dashSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: {
    xs: backrs.spacing['spacing-sm'],
    md: backrs.spacing['spacing-md'],
  },
  color: backrs.colors['Gray/300'],
});

const otpContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: {
    xs: backrs.spacing['spacing-sm'],
    md: backrs.spacing['spacing-md'],
  },
});

const textFieldSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: {
    xs: '2.75rem',
    md: '4rem',
  },
  height: {
    xs: '2.75rem',
    md: '4rem',
  },
  minWidth: {
    xs: '2.75rem',
    md: '4rem',
  },
  minHeight: {
    xs: '2.75rem',
    md: '4rem',
  },
  '& .MuiInputBase-root': {
    outline: `2px solid ${backrs.colors['Gray/300']}`,
    outlineOffset: '-2px',
    padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-md']}`,
    height: '100%',
    width: '100%',
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    color: backrs.colors['Colors/Text/text-brand-tertiary-alt'],
    ...typography['display-lg-medium'],
    fontSize: {
      xs: '1.825rem',
      md: '2.25rem',
    },
    height: '100%',
    width: '100%',
  },
  '& .MuiInputBase-root.Mui-focused': {
    outline: `2px solid ${backrs.colors['Gray/300']}`,
    outlineOffset: '2px',
    padding: '2px',
  },
});
